import {useRef, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Swal from 'sweetalert2';
import {LocalizationProvider, TimePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DemoItem} from '@mui/x-date-pickers/internals/demo';
import {fetchPatientDetailsUuid, postPatientReport} from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import {RenderSignature} from '../utils/templateUtils';
import AnalysisTable from './AnalysisTable';
import FigmaHeader from './FigmaHeader/FigmaHeader';
import MedicalReportTemplate, {formatDateToDDMMYYYY} from './MedicalReportTemplate';
import embryologyLogo from '../assets/images/embryology-logo.png';

const isValidBranchAwareUhid = value => /^(ASCAS|VDASCAS)\d+$/i.test((value || '').trim());

// Common styles
const commonStyles = {
  input: {
    border: '1px solid #b0a4a4ff',
    outline: 'none',
    padding: '10px',
    backgroundColor: 'white',
    width: '100%'
  },
  label: {
    fontWeight: 600,
    fontSize: '14px'
  },
  separator: {
    fontWeight: 600,
    fontSize: '14px'
  },
  section: {
    maxWidth: '97%',
    margin: '0 auto'
  }
};

const MORPHOLOGY_ROWS = [
  {
    label: 'Normal',
    name: 'normalForms',
    type: 'text',
    unit: '%',
    width: '150px',
    reference: '>4%'
  },
  {
    label: 'Abnormal',
    name: 'abnormalForms',
    type: 'text',
    unit: '%',
    width: '150px'
  }
];

// Section header component
const SectionHeader = ({title, className = 'spermiogram-head'}) => (
  <h2
    style={{
      textAlign: 'center',
      backgroundColor: '#f7f7f7',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: '16px',
      fontWeight: '600',
      padding: '5px 0 5px 8px',
      margin: '8px 0'
    }}
    className={className}>
    {title}
  </h2>
);

// Reusable FormField component
const FormField = ({label, type = 'text', name, value, onChange, show, options = [], width = '200px', labelWidth = '160px'}) => {
  const inputStyle = {...commonStyles.input, width};

  if (show) {
    return (
      <div style={{display: 'grid', gridTemplateColumns: `${labelWidth} 16px 1fr`, alignItems: 'center', gap: '16px'}}>
        <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
        <span style={commonStyles.separator}>:</span>
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value}</span>
      </div>
    );
  }

  return (
    <div style={{display: 'grid', gridTemplateColumns: `${labelWidth} 16px ${width}`, alignItems: 'center', gap: '16px'}}>
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
      <span style={commonStyles.separator}>:</span>
      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={inputStyle}>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
      )}
    </div>
  );
};

// Patient Info Section component
const PatientInfoSection = ({patientNumber, partnerType, show, handleChange, valueChange}) => {
  const genderOptions = [
    {value: '', label: 'Select Gender'},
    {value: 'Male', label: 'Male'},
    {value: 'Female', label: 'Female'},
    {value: 'Others', label: 'Others'},
    {value: 'Decline to State', label: 'Decline to State'}
  ];

  return (
    <div className='' style={{width: '100%', display: 'block'}} id={`patientInfo${patientNumber}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '3px',
          backgroundColor: '#f7f7f7',
          borderRadius: '6px',
          padding: '16px 8px'
        }}>
        <FormField label='Patient ID' name={`patientId${patientNumber}`} value={valueChange[`patientId${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
        <FormField label={`${partnerType} Partner Name`} name={`patientName${patientNumber}`} value={valueChange[`patientName${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
        <FormField label='Gender' type='select' name={`gender${patientNumber}`} value={valueChange[`gender${patientNumber}`]} onChange={handleChange} show={show} options={genderOptions} width='250px' labelWidth='125px' />
        <FormField label='Age' name={`age${patientNumber}`} value={valueChange[`age${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
      </div>
    </div>
  );
};

// Morphology Section Component
const MorphologySection = ({show, handleChange, valueChange}) => (
  <>
    <SectionHeader title='🧫 Morphology' />
    <div style={{marginBottom: '20px'}}>
      <AnalysisTable
        headers={['Parameter', 'Result']}
        rows={MORPHOLOGY_ROWS}
        show={show}
        onChange={handleChange}
        values={valueChange}
        commonStyles={commonStyles}
      />
    </div>
  </>
);



// Comments Section Component
const AdviceSection = ({show, handleChange, valueChange}) => (
  <div style={{marginBottom: '10px'}}>
    <SectionHeader title='📋 Comments' />
    {!show ? (
      <textarea
        name='comments'
        value={valueChange.comments}
        onChange={handleChange}
        style={{
          ...commonStyles.input,
          width: '100%',
          minHeight: '60px',
          padding: '10px',
          fontFamily: 'Arial, sans-serif',
          marginTop: '5px'
        }}
        placeholder='Enter clinical comments and recommendations...'
      />
    ) : (
      valueChange.comments && (
        <div style={{border: '1px solid #ddd', padding: '10px', minHeight: '40px', backgroundColor: '#f9f9f9', whiteSpace: 'pre-wrap', fontFamily: 'Arial, sans-serif', marginTop: '5px'}}>
          {valueChange.comments}
        </div>
      )
    )}
  </div>
);

function DIFReport(props) {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    gender1: 'Male',
    gender2: 'Male',
    bloodGroup1: 'A+',
    bloodGroup2: 'A+',
    coupleId: '',

    // DFI specific fields
    daysAbstinence: '',
    collectionDate: '',
    collectionTime: '',
    examinationTime: '',
    reportingDate: '',

    // Semen analysis fields
    spermConcentration: '',
    totalSpermNumber: '',
    progressiveMotility: '',
    slowProgressiveMotility: '',
    nonProgressiveMotility: '',
    immotileSperm: '',
    fructose: '',
    totalMotileProgressive: '',
    totalMotility: '',
    vitality: '',
    normalMorphology: '',

    // Macroscopic analysis
    volume: '',
    pH: '',
    appearance: '',
    viscosity: '',
    liquefactionTime: '',

    // Microscopic analysis
    agglutination: '',
    aggregation: '',
    roundCells: '',
    otherCells: '',
    morphologyNF: '',

    // Morphology
    morphology: {
      normalForms: '',
      abnormalForms: '',
      headAbnormalities: '',
      midpieceAbnormalities: '',
      tailAbnormalities: '',
      cytoplasmicDroplets: ''
    },

    // Impression & Comments
    comments: '',

    // DFI Analysis
    totalSpermatozoaAnalyzed: '',
    nonFragmentedHalos: '',
    fragmentedHalos: '',
    observedDFI: '',
    dfiCategory: '',

    // DFI Analysis Table fields
    largeHaloFragmented: '',
    largeHaloFragmentedDFI: '',
    mediumHaloNonFragmented: '',
    mediumHaloNonFragmentedDFI: '',
    smallHaloTotal: '',
    smallHaloTotalDFI: '',
    noHaloDFI: '',
    noHaloDFIDFI: '',
    totalSpermCounted: '',
    totalSpermCountedDFI: '',

    // Reporting
    reporting: {
      androtechName: '',
      consultantName: '',
      technician: '',
      technicianOtherName: '',
      clinicianSignature: ''
    }
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  // Calculate semen totals automatically
  useEffect(() => {
    const volume = parseFloat(valueChange.volume) || 0;
    const progressiveMotility = parseFloat(valueChange.progressiveMotility) || 0;
    const slowProgressiveMotility = parseFloat(valueChange.slowProgressiveMotility) || 0;
    const nonProgressiveMotility = parseFloat(valueChange.nonProgressiveMotility) || 0;
    const spermConcentration = parseFloat(valueChange.spermConcentration) || 0;

    const totalMotility = progressiveMotility + slowProgressiveMotility + nonProgressiveMotility;
    const totalSpermNumber = volume * spermConcentration;
    const tmsc = volume * spermConcentration * (totalMotility / 100);
    const nextValues = {
      totalSpermNumber: totalSpermNumber > 0 ? totalSpermNumber.toFixed(2) : '',
      totalMotility: totalMotility > 0 ? totalMotility.toFixed(2) : '',
      totalMotileProgressive: tmsc > 0 ? tmsc.toFixed(2) : ''
    };

    if (
      nextValues.totalSpermNumber !== valueChange.totalSpermNumber ||
      nextValues.totalMotility !== valueChange.totalMotility ||
      nextValues.totalMotileProgressive !== valueChange.totalMotileProgressive
    ) {
      setvalueChange(prev => ({...prev, ...nextValues}));
    }
  }, [valueChange.volume, valueChange.progressiveMotility, valueChange.slowProgressiveMotility, valueChange.nonProgressiveMotility, valueChange.spermConcentration, valueChange.totalSpermNumber, valueChange.totalMotility, valueChange.totalMotileProgressive]);

  const handleChange = e => {
    const text = e.target.value;

    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (isValidBranchAwareUhid(text)) {
        const fetchData = async () => {
          const payload = {p_uhid: text};
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            const isSinglePatient = response.payload.data.data.length === 1;
            if (isSinglePatient) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({...valueChange, patientName1: namePat1, age1: data?.age, gender1: data?.gender, patientId1: text});
              } else {
                setPatientTwo_UUId(data?.id);
                setvalueChange({...valueChange, patientName2: namePat1, age2: data?.age, gender2: data?.gender, patientId2: text});
              }
            } else {
              const data = response.payload.data.data[0].gender === 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender === 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;
              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({...valueChange, patientName1: namePat1, patientName2: namePat2, age1: data?.age, age2: data2?.age, gender1: data?.gender, gender2: data2?.gender, patientId1: data?.p_uhid, patientId2: data2?.p_uhid});
            }
            props.loaderchange('false');
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => { props.popupalert('false'); }, 2000);
          }
        };
        fetchData();
      } else {
        setvalueChange({...valueChange, [e.target.name]: text});
      }
    } else if (
      e.target.name === 'androtechName' ||
      e.target.name === 'consultantName' ||
      e.target.name === 'technician' ||
      e.target.name === 'technicianOtherName' ||
      e.target.name === 'clinicianSignature'
    ) {
      setvalueChange({...valueChange, reporting: {...valueChange.reporting, [e.target.name]: text}});
    } else if (
      e.target.name === 'normalForms' ||
      e.target.name === 'abnormalForms' ||
      e.target.name === 'headAbnormalities' ||
      e.target.name === 'midpieceAbnormalities' ||
      e.target.name === 'tailAbnormalities' ||
      e.target.name === 'cytoplasmicDroplets'
    ) {
      setvalueChange({...valueChange, morphology: {...valueChange.morphology, [e.target.name]: text}});
    } else {
      setvalueChange({...valueChange, [e.target.name]: text});
    }
  };

  const handletest = async () => {
    try {
      if (
        patientOne_UUId !== '' &&
        valueChange.patientId1 !== '' &&
        isValidBranchAwareUhid(valueChange.patientId1) &&
        (valueChange.patientId2 !== '' ? valueChange.patientId1 !== valueChange.patientId2 : true)
      ) {
        setshow(true);
        setTimeout(async () => {
          const htmlPayload = document.getElementById('reportcreate').innerHTML;
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'DFI Report'
          };

          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');
            Swal.fire({title: 'Successful.', text: 'Report added successful.', icon: 'success', showConfirmButton: false, timer: 1500});
          } else {
            setshow(false);
            props.loaderchange(false);
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => { props.popupalert(false); }, 2000);
          }
        }, 3000);
      } else {
        Swal.fire({title: 'Error.', text: 'Please enter valid patient id.', icon: 'error', showConfirmButton: false, timer: 1500});
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const sideactive = useSelector(state => state.counter.sidebarnav);

  // MUI TimePicker handler
  const handleMuiTimeChange = (e, name) => {
    let hours = e.$H, mins = e.$m, ampm = 'AM';
    let temMin = e.$m;
    temMin < 10 ? (mins = '0' + temMin) : (mins = temMin);
    if (e.$H >= 12) {
      if (e.$H === 12) { hours = 12; ampm = 'PM'; }
      else { let temHour = e.$H - 12; temHour < 10 ? (hours = '0' + temHour) : (hours = temHour); ampm = 'PM'; }
    } else if (e.$H === 0) { hours = 12; ampm = 'AM'; }
    else { let temHour = e.$H; temHour < 10 ? (hours = '0' + temHour) : (hours = temHour); ampm = 'AM'; }
    setvalueChange({...valueChange, [name]: `${hours}-${mins}-${ampm}`});
  };

  const renderMuiTime = name => (
    <div className={!show ? 'show' : 'hide'}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoItem>
          <TimePicker onChange={e => handleMuiTimeChange(e, name)} />
        </DemoItem>
      </LocalizationProvider>
    </div>
  );

  const macroscopicRows = [
    {label: 'Volume', name: 'volume', type: 'text', unit: '(ml)', reference: '>=1.5 ml', width: '150px'},
    {label: 'Appearance and Colour', name: 'appearance', type: 'text', reference: 'Whitish/ Greyish Opalescent', width: '150px'},
    {label: 'Viscosity', name: 'viscosity', type: 'text', reference: 'No Reference', width: '150px'},
    {label: 'pH', name: 'pH', type: 'text', reference: '7.2-7.8', width: '150px'},
    {label: 'Time of Liquefaction', name: 'liquefactionTime', type: 'text', unit: '(Mins)', reference: '<60 Mins', width: '150px'}
  ];

  const microscopicRows = [
    {label: 'Sperm Concentration', name: 'spermConcentration', type: 'text', unit: '(millions/ml)', reference: '>=16 millions/ml', width: '150px'},
    {label: 'Total Sperm Number (per ejaculate)', name: 'totalSpermNumber', type: 'text', unit: '(million)', reference: '>=39 million', width: '150px'},
    {label: 'Rapid Progressive Motility(a)', name: 'progressiveMotility', type: 'text', unit: '%', reference: '>=30% RPR+SPR(A+B)', width: '150px'},
    {label: 'Slow Progressive Motility(b)', name: 'slowProgressiveMotility', type: 'text', unit: '%', reference: '<=42% PR+NP(A+B+C)', width: '150px'},
    {label: 'Non-Progressive Motility(c)', name: 'nonProgressiveMotility', type: 'text', unit: '%', reference: '', width: '150px'},
    {label: 'Total Motility', name: 'totalMotility', type: 'text', unit: '%', reference: '', width: '150px'},
    {label: 'Immotile Sperm', name: 'immotileSperm', type: 'text', unit: '%', reference: '', width: '150px'},
    {label: 'Fructose', name: 'fructose', type: 'text', reference: '', width: '150px'},
    {label: 'Total Motile Progressive(TMSC)', name: 'totalMotileProgressive', type: 'text', unit: 'million', reference: '', width: '150px'},
    {label: 'Vitality', name: 'vitality', type: 'text', unit: '%', reference: '>54%', width: '150px'},
    {label: 'Agglutination', name: 'agglutination', type: 'text', reference: '', width: '150px'},
    {label: 'Aggregation', name: 'aggregation', type: 'text', reference: '', width: '150px'},
    {label: 'Round Cells / HPF', name: 'roundCells', type: 'text', unit: 'cells/HPF', reference: '', width: '150px'},
    {label: 'Other Cells / Debris', name: 'otherCells', type: 'text', reference: '', width: '150px'}
  ];

  const printData = {
    patientOneId: valueChange.patientId1,
    patientTwoId: valueChange.patientId2,
    patientOneName: valueChange.patientName1,
    patientTwoName: valueChange.patientName2,
    genderOne: valueChange.gender1,
    genderTwo: valueChange.gender2,
    patientOneAge: valueChange.age1,
    patientTwoAge: valueChange.age2,
    date: new Date().toLocaleDateString('en-IN'),
    doctor: valueChange.reporting.consultantName,
    modeOfCollection: 'Ejaculate',
    timeOfExamination: valueChange.examinationTime || '',
    completeCollection: valueChange.completeSample,
    sampleCollection: 'Fresh',
    timeOfCollection: valueChange.collectionTime || '',
    sampleCollectionDate: valueChange.collectionDate || new Date().toLocaleDateString('en-IN'),
    abstinencePeriod: valueChange.daysAbstinence ? `${valueChange.daysAbstinence} Days` : '',
    technician: valueChange.reporting.technician,
    technicianOtherName: valueChange.reporting.technicianOtherName,
    androtechName: valueChange.reporting.androtechName,
    consultantName: valueChange.reporting.consultantName,
    clinicianSignature: valueChange.reporting.clinicianSignature,
    androtechSignature: ''
  };

  const printSections = [
    {
      title: 'Collection Details',
      layout: 'custom-grid',
      data: [
        [
          {label: 'Days of Abstinence', value: valueChange.daysAbstinence ? `${valueChange.daysAbstinence} Days` : ''},
          {label: 'Date of Collection', value: formatDateToDDMMYYYY(valueChange.collectionDate) || ''},
          {label: 'Date of Reporting', value: formatDateToDDMMYYYY(valueChange.reportingDate) || ''}
        ],
        [
          {label: 'Time of Collection', value: valueChange.collectionTime ? valueChange.collectionTime.replace(/-/g, ':') : ''},
          {label: 'Time of Examination', value: valueChange.examinationTime ? valueChange.examinationTime.replace(/-/g, ':') : ''}
        ]
      ]
    },
    {
      title: 'Macroscopic Examination',
      layout: 'custom-grid',
      data: [
        [
          {label: 'Volume', value: valueChange.volume ? `${valueChange.volume} (ml)` : ''},
          {label: 'Appearance and Colour', value: valueChange.appearance || ''},
          {label: 'Viscosity', value: valueChange.viscosity || ''}
        ],
        [
          {label: 'pH', value: valueChange.pH || ''},
          {label: 'Time of Liquefaction', value: valueChange.liquefactionTime ? `${valueChange.liquefactionTime} (Mins)` : ''}
        ]
      ]
    },
    {
      title: 'Microscopic Examination',
      layout: 'custom-grid',
      data: [
        [
          {label: 'Sperm Concentration', value: valueChange.spermConcentration ? `${valueChange.spermConcentration} (million/ml)` : ''},
          {label: 'Total Sperm Number (per ejaculate)', value: valueChange.totalSpermNumber ? `${valueChange.totalSpermNumber} (million)` : ''},
          {label: 'Rapid Progressive Motility(a)', value: valueChange.progressiveMotility ? `${valueChange.progressiveMotility} %` : ''},
          {label: 'Slow Progressive Motility(b)', value: valueChange.slowProgressiveMotility ? `${valueChange.slowProgressiveMotility} %` : ''},
          {label: 'Non-Progressive Motility(c)', value: valueChange.nonProgressiveMotility ? `${valueChange.nonProgressiveMotility} %` : ''},
          {label: 'Total Motility', value: valueChange.totalMotility ? `${valueChange.totalMotility} %` : ''},
          {label: 'Immotile Sperm', value: valueChange.immotileSperm ? `${valueChange.immotileSperm} %` : ''},
          {label: 'Fructose', value: valueChange.fructose || ''}
        ],
        [
          {label: 'Total Motile Progressive(TMSC)', value: valueChange.totalMotileProgressive ? `${valueChange.totalMotileProgressive} million` : ''},
          {label: 'Vitality', value: valueChange.vitality ? `${valueChange.vitality} %` : ''},
          {label: 'Agglutination', value: valueChange.agglutination || ''},
          {label: 'Aggregation', value: valueChange.aggregation || ''},
          {label: 'Round Cells / HPF', value: valueChange.roundCells ? `${valueChange.roundCells} (cells/HPF)` : ''},
          {label: 'Other Cells / Debris', value: valueChange.otherCells || ''}
        ]
      ]
    },
    {
      title: 'Morphology',
      layout: 'custom-grid',
      data: [
        [
          {label: 'Normal', value: valueChange.morphology?.normalForms ? `${valueChange.morphology.normalForms}%` : ''},
          {label: 'Abnormal', value: valueChange.morphology?.abnormalForms ? `${valueChange.morphology.abnormalForms}%` : ''}
        ]
      ]
    },
    {
      title: '',
      noWrapper: true,
      layout: 'full-table',
      headers: ['', 'TOTAL NUMBERS', 'DFI SCORE', ''],
      data: [
        [{value: 'Large Halo', bold: true}, {value: valueChange.largeHaloFragmented || ''}, {value: 'Fragmented (In numbers)'}, {value: valueChange.largeHaloFragmentedDFI || ''}],
        [{value: 'Medium Halo', bold: true}, {value: valueChange.mediumHaloNonFragmented || ''}, {value: 'Non Fragmented (In numbers)'}, {value: valueChange.mediumHaloNonFragmentedDFI || ''}],
        [{value: 'Small Halo', bold: true}, {value: valueChange.smallHaloTotal || ''}, {value: 'Total'}, {value: valueChange.smallHaloTotalDFI || ''}],
        [{value: 'No Halo', bold: true}, {value: valueChange.noHaloDFI || ''}, {value: 'DFI %'}, {value: valueChange.noHaloDFIDFI || ''}],
        [{value: 'Total Sperm Counted', bold: true}, {value: valueChange.totalSpermCounted || ''}, {value: 'Result'}, {value: valueChange.totalSpermCountedDFI || ''}],
        [{value: 'Results', bold: true}, {value: valueChange.dfiCategory || ''}, {value: ''}, {value: ''}]
      ]
    },
    {
      title: 'REFERENCE RANGES',
      layout: 'table',
      data: [
        [{value: 'DFI Reference Ranges', bold: true}, {value: '• < 15 → Normal • 15–30 → Moderate • > 30 → High', bold: false}]
      ]
    }
  ];

  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>Dashboard /</a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>&nbsp;Patient Reports</a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        <h1>🧬 DNA FRAGMENTATION INDEX (DFI) REPORT - SCD METHOD</h1>
        <div style={{backgroundColor: 'white', maxWidth: '100%'}} className='patientreport' id='reportcreate'>
          {show ? (
            <MedicalReportTemplate
              title='DNA FRAGMENTATION INDEX (DFI) REPORT - SCD METHOD'
              clinicLogo={embryologyLogo}
              showDateTopRight={true}
              patientData={printData}
              sections={printSections}
              comments={valueChange.comments}
              referenceValues={[]}
              footerText={`Lab Reference: DFI-2026-${new Date().toISOString().slice(0, 10).replace(/-/g, '')} | WHO Reference Values 2021 6th Edition`}
            />
          ) : (
            <>
              <FigmaHeader />

              {/* Patient & Couple Details */}
              <div
                style={{display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '3px', border: '1px solid #000', marginBottom: '10px'}}
                className='report-template-wrap1'>
                <PatientInfoSection patientNumber={1} partnerType='Male' show={show} handleChange={handleChange} valueChange={valueChange} />
                <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
                  <PatientInfoSection patientNumber={2} partnerType='Female' show={show} handleChange={handleChange} valueChange={valueChange} />
                </div>
              </div>
              <h2 style={{textAlign: 'center', fontWeight: '600', backgroundColor: '#f7f7f7', padding: '10px'}}>SEMEN ANALYSIS &amp; DNA FRAGMENTATION REPORT – SCD METHOD</h2>

              <div style={{padding: '10px'}}>
                {/* Basic Information */}
                <div style={{marginBottom: '10px'}}>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                      <FormField label='Days of Abstinence:' type='text' name='daysAbstinence' value={valueChange.daysAbstinence} onChange={handleChange} show={show} width='200px' labelWidth='160px' />
                      <FormField label='Date of Collection:' type='date' name='collectionDate' value={valueChange.collectionDate} onChange={handleChange} show={show} width='200px' labelWidth='160px' />
                      <FormField label='Date of Reporting:' type='date' name='reportingDate' value={valueChange.reportingDate} onChange={handleChange} show={show} width='200px' labelWidth='160px' />
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                      {!show ? (
                        <div style={{display: 'grid', gridTemplateColumns: '160px 16px 200px', alignItems: 'center', gap: '16px'}}>
                          <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Collection:</span>
                          <span style={commonStyles.separator}>:</span>
                          {renderMuiTime('collectionTime')}
                        </div>
                      ) : (
                        <div style={{display: 'grid', gridTemplateColumns: '160px 16px 200px', alignItems: 'center', gap: '16px'}}>
                          <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Collection:</span>
                          <span style={commonStyles.separator}>:</span>
                          <span style={{fontSize: '14px', textAlign: 'left'}}>{valueChange.collectionTime ? valueChange.collectionTime.replace(/-/g, ':') : ''}</span>
                        </div>
                      )}
                      {!show ? (
                        <div style={{display: 'grid', gridTemplateColumns: '160px 16px 200px', alignItems: 'center', gap: '16px'}}>
                          <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Examination:</span>
                          <span style={commonStyles.separator}>:</span>
                          {renderMuiTime('examinationTime')}
                        </div>
                      ) : (
                        <div style={{display: 'grid', gridTemplateColumns: '160px 16px 200px', alignItems: 'center', gap: '16px'}}>
                          <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Examination:</span>
                          <span style={commonStyles.separator}>:</span>
                          <span style={{fontSize: '14px', textAlign: 'left'}}>{valueChange.examinationTime ? valueChange.examinationTime.replace(/-/g, ':') : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Macroscopic Analysis */}
                <div style={{marginBottom: '10px'}}>
                  <SectionHeader title='⚗️ Macroscopic Examination' />
                  <AnalysisTable headers={['Parameter', 'Result', 'WHO Reference']} rows={macroscopicRows} show={show} onChange={handleChange} values={valueChange} commonStyles={commonStyles} />
                </div>

                {/* Microscopic Analysis */}
                <div style={{marginBottom: '10px'}}>
                  <SectionHeader title='🔬 Microscopic Examination' />
                  <AnalysisTable headers={['Parameter', 'Result', 'WHO Reference']} rows={microscopicRows} show={show} onChange={handleChange} values={valueChange} commonStyles={commonStyles} />
                </div>

                {/* Morphology */}
                <div style={{marginBottom: '10px'}}>
                  <MorphologySection show={show} handleChange={handleChange} valueChange={valueChange} />
                </div>

                {/* DNA Fragmentation Index */}
                <div style={{marginBottom: '10px'}}>
                  <div style={{padding: '10px'}}>
                    <div style={{marginBottom: '16px'}}>
                      {/* DFI Analysis Table */}
                      <div style={{marginBottom: '15px', overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', marginTop: '10px'}}>
                          <thead>
                            <tr style={{backgroundColor: '#f7f7f7'}}>
                              <th style={{border: '1px solid #ddd', padding: '8px', fontWeight: '600', textAlign: 'left'}}></th>
                              <th style={{border: '1px solid #ddd', padding: '8px', fontWeight: '600', textAlign: 'left'}}>TOTAL NUMBERS</th>
                              <th style={{border: '1px solid #ddd', padding: '8px', fontWeight: '600', textAlign: 'left'}}>DFI SCORE</th>
                              <th style={{border: '1px solid #ddd', padding: '8px', fontWeight: '600', textAlign: 'left'}}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              {label: 'Large Halo', totalName: 'largeHaloFragmented', scoreName: 'largeHaloFragmentedDFI', scoreLabel: 'Fragmented (In numbers)'},
                              {label: 'Medium Halo', totalName: 'mediumHaloNonFragmented', scoreName: 'mediumHaloNonFragmentedDFI', scoreLabel: 'Non Fragmented (In numbers)'},
                              {label: 'Small Halo', totalName: 'smallHaloTotal', scoreName: 'smallHaloTotalDFI', scoreLabel: 'Total'},
                              {label: 'No Halo', totalName: 'noHaloDFI', scoreName: 'noHaloDFIDFI', scoreLabel: 'DFI %'},
                              {label: 'Total Sperm Counted', totalName: 'totalSpermCounted', scoreName: 'totalSpermCountedDFI', scoreLabel: 'Result'}
                            ].map(row => (
                              <tr key={row.label}>
                                <td style={{border: '1px solid #ddd', padding: '8px', fontWeight: '600'}}>{row.label}</td>
                                <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                  <input type='text' className={`createTime ${show ? 'hide' : 'show'}`} name={row.totalName} value={valueChange[row.totalName] || ''} onChange={e => handleChange(e)} style={{...commonStyles.input, width: '100%'}} />
                                  <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange[row.totalName]}</span>
                                </td>
                                <td style={{border: '1px solid #ddd', padding: '8px', fontWeight: '600'}}>{row.scoreLabel}</td>
                                <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                  <input type='text' className={`createTime ${show ? 'hide' : 'show'}`} name={row.scoreName} value={valueChange[row.scoreName] || ''} onChange={e => handleChange(e)} style={{...commonStyles.input, width: '100%'}} />
                                  <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange[row.scoreName]}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap'}}>
                          <span style={{fontWeight: 600}}>Reference:</span>
                          <span>Normal &lt; 15%</span>
                          <span>15-25% Moderate</span>
                          <span>&gt; 25% High</span>
                        </div>
                      </div>

                      <div style={{display: 'flex', alignItems: 'center', gap: '16px', marginTop: '10px'}}>
                        <span style={{fontWeight: 600, width: '340px', display: 'inline-block'}}>Results:</span>
                        <select className={`createTime ${show ? 'hide' : 'show'}`} name='dfiCategory' value={valueChange.dfiCategory || ''} onChange={e => handleChange(e)} style={{...commonStyles.input, width: '120px'}}>
                          <option value=''>Select</option>
                          <option value='Normal'>Normal</option>
                          <option value='Moderate'>Moderate</option>
                          <option value='High'>High</option>
                        </select>
                        <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.dfiCategory}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Impression & Comments */}
                <AdviceSection show={show} handleChange={handleChange} valueChange={valueChange} />

                {/* Signature Section */}
                <RenderSignature show={show} valueChange={valueChange} handleChange={handleChange} commonStyles={commonStyles} />
              </div>
            </>
          )}
        </div>

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
}

export default DIFReport;
